import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Styles from './Styles.module.css';
import VideoCard from './VideoCard/VideoCard';

const VideoSection = ({ rightPanelOpen, peers = [], peerNames = [], localStream, videoOn }) => {
  const { t } = useTranslation();
  const localThumbRef = useRef();
  const dominantVideoRef = useRef();
  const peerStreamsRef = useRef({}); // peerIdx -> MediaStream
  const [dominantIdx, setDominantIdx] = useState(-1); // -1 = local

  // Keep local thumbnail video up to date
  useEffect(() => {
    if (localStream && localThumbRef.current) {
      localThumbRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Update dominant video when dominantIdx or localStream changes
  useEffect(() => {
    if (!dominantVideoRef.current) return;
    if (dominantIdx === -1) {
      dominantVideoRef.current.srcObject = localStream || null;
    } else {
      dominantVideoRef.current.srcObject = peerStreamsRef.current[dominantIdx] || null;
    }
  }, [dominantIdx, localStream]);

  const handlePeerStream = useCallback((idx, stream) => {
    peerStreamsRef.current[idx] = stream;
    // If this peer is currently dominant, push stream to dominant video
    if (dominantIdx === idx && dominantVideoRef.current) {
      dominantVideoRef.current.srcObject = stream;
    }
    // Auto-switch dominant to first peer when they connect
    if (idx === 0) {
      setDominantIdx(0);
      if (dominantVideoRef.current) dominantVideoRef.current.srcObject = stream;
    }
  }, [dominantIdx]);

  const handleSelectTile = (idx) => {
    setDominantIdx(idx);
    if (!dominantVideoRef.current) return;
    if (idx === -1) {
      dominantVideoRef.current.srcObject = localStream || null;
    } else {
      dominantVideoRef.current.srcObject = peerStreamsRef.current[idx] || null;
    }
  };

  const dominantLabel = dominantIdx === -1 ? t('screens.video.group.you') : (peerNames[dominantIdx] || t('screens.video.group.participant', { index: dominantIdx + 1 }));
  const showDominantVideo = dominantIdx !== -1 || videoOn;

  return (
    <div className={`${Styles.videoSection} ${rightPanelOpen ? Styles.panelOpen : ''}`}>
      {/* Dominant speaker area */}
      <div className={Styles.dominantWrap}>
        <div className={Styles.dominantVideo}>
          {showDominantVideo ? (
            <video
              ref={dominantVideoRef}
              autoPlay
              playsInline
              muted={dominantIdx === -1}
              className={Styles.dominantVideoEl}
            />
          ) : (
            <div className={Styles.avatarPlaceholder}>
              <div className={Styles.avatarCircle}>Y</div>
              <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 12 }}>{t('screens.video.group.cameraOff')}</div>
            </div>
          )}
          <div className={Styles.dominantNameBadge}>
            <span className={Styles.dominantNameText}>{dominantLabel}</span>
          </div>
        </div>
      </div>

      {/* Thumbnail strip */}
      <div className={Styles.thumbStrip}>
        {/* Local tile */}
        <div
          className={`${Styles.thumbTile} ${dominantIdx === -1 ? Styles.activeTile : ''}`}
          onClick={() => handleSelectTile(-1)}
        >
          {videoOn ? (
            <video
              ref={localThumbRef}
              autoPlay
              playsInline
              muted
              className={Styles.thumbVideoEl}
            />
          ) : (
            <div className={Styles.thumbAvatarWrap}>
              <div className={`${Styles.thumbAvatar} ${Styles.burg}`}>Y</div>
            </div>
          )}
          <div className={Styles.thumbName}>{t('screens.video.group.you')}</div>
          <div className={Styles.youBadge}>{t('screens.video.group.youBadge')}</div>
        </div>

        {/* Peer tiles */}
        {peers.map((peer, index) => (
          <VideoCard
            key={index}
            peer={peer}
            label={peerNames[index] || t('screens.video.group.participant', { index: index + 1 })}
            isActive={dominantIdx === index}
            onStream={(stream) => handlePeerStream(index, stream)}
            onClick={() => handleSelectTile(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default VideoSection;
